/**
 * Admin Affiliate Payout Data Fetching Hooks
 *
 * Custom hooks using React Query for the Affiliate Payments review queue:
 * list commission periods, approve/reject payouts, and read per-period
 * status-event history (eng-00099).
 */

import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import {
  approveAdminAffiliatePayout,
  fetchAdminAffiliatePayoutEvents,
  fetchAdminAffiliatePayouts,
  rejectAdminAffiliatePayout,
} from "../utils/adminApi";

export const AFFILIATE_PAYOUTS_QUERY_KEY_PREFIX = ["admin", "affiliate-payouts"];

export const useAdminAffiliatePayouts = ({ status = "", search = "", limit = 25, offset = 0, enabled = true } = {}) => {
  return useQuery({
    queryKey: [...AFFILIATE_PAYOUTS_QUERY_KEY_PREFIX, "list", { status, search, limit, offset }],
    queryFn: () => fetchAdminAffiliatePayouts({ status, search, limit, offset }),
    enabled,
    refetchOnWindowFocus: false,
    // Keep the previous page visible while a filter/page change refetches.
    placeholderData: (previousData) => previousData,
  });
};

export const useAdminAffiliatePayoutEvents = (periodId, { enabled = true } = {}) => {
  return useQuery({
    queryKey: [...AFFILIATE_PAYOUTS_QUERY_KEY_PREFIX, "events", { periodId }],
    queryFn: () => fetchAdminAffiliatePayoutEvents(periodId),
    enabled: enabled && !!periodId,
    refetchOnWindowFocus: false,
  });
};

export const useApproveAffiliatePayout = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (periodId) => approveAdminAffiliatePayout(periodId),
    onSuccess: async () => {
      // Invalidate both the list and every expanded history panel: an approve
      // changes the row status AND appends status events.
      await queryClient.invalidateQueries({ queryKey: AFFILIATE_PAYOUTS_QUERY_KEY_PREFIX });
    },
  });
};

export const useRejectAffiliatePayout = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ periodId, note }) => rejectAdminAffiliatePayout(periodId, note),
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: AFFILIATE_PAYOUTS_QUERY_KEY_PREFIX });
    },
  });
};
